/**
 * ClauseAddRow — one row at the end of a SELECT's cards offering its EMPTY
 * optional clauses in SQL order: "Add  + Where  + Group by  + Having
 * + Order by  + Limit". It stands in for the "— none" row each empty clause
 * used to take: the sections render with `hideEmpty`, so a clause that gets
 * content appears in its SQL position and leaves this row.
 *
 * Each entry is that clause's own add control under a short label — the same
 * pickers, test ids and analytics. Having is offered once there is a GROUP BY
 * (as the empty HAVING row was); Limit reads as the dialect keyword (Top /
 * Limit / Fetch first) and, like the LIMIT row, waits for the schema snapshot.
 * Renders nothing without an edit context or when every clause has content.
 */
import type { ReactElement } from 'react';

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { vqeCopy } from '../../ux/vqeCopy';
import { CLAUSE_TITLE_COLUMN } from './ClauseCard';
import type { SelectOutline } from './extractSelectOutline';
import { FilterClauseAddButton } from './FilterPills';
import { GroupByAddPopover } from './GroupByAddPopover';
import { LimitAddButton } from './LimitOffsetPanel';
import { hasLimitClause, limitClauseKeyword } from './limitClauseKeyword';
import { OrderByAddPopover } from './OrderByAddPopover';

export function ClauseAddRow({
  bufferText,
  outline,
  options,
  editContext,
}: {
  readonly bufferText: string;
  readonly outline: SelectOutline;
  readonly options: ParseOptions;
  readonly editContext?: VqeEditContext;
}): ReactElement | null {
  if (!editContext) return null;
  const where = !outline.where;
  const groupBy = !outline.groupBy;
  const having = !outline.having && !!outline.groupBy;
  const orderBy = !outline.orderBy;
  const limit = !hasLimitClause(outline) && !!editContext.service.getSchemaSnapshot();
  if (!where && !groupBy && !having && !orderBy && !limit) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-x-1 gap-y-1 rounded-md border border-dashed border-border px-3 py-1"
      data-testid="vqe-clause-add-row"
    >
      <span className={`${CLAUSE_TITLE_COLUMN} text-xs font-semibold uppercase tracking-wide text-muted-foreground`}>
        {vqeCopy.clauseAdd.lead}
      </span>
      {where ? <FilterClauseAddButton editContext={editContext} kind="where" label={vqeCopy.clauseAdd.where} /> : null}
      {groupBy ? <GroupByAddPopover editContext={editContext} triggerLabel={vqeCopy.clauseAdd.groupBy} /> : null}
      {having ? <FilterClauseAddButton editContext={editContext} kind="having" label={vqeCopy.clauseAdd.having} /> : null}
      {orderBy ? (
        <OrderByAddPopover editContext={editContext} itemCount={0} triggerLabel={vqeCopy.clauseAdd.orderBy} />
      ) : null}
      {limit ? (
        <LimitAddButton
          editContext={editContext}
          engine={options.engine}
          label={limitClauseKeyword(outline, options.engine, bufferText)}
        />
      ) : null}
    </div>
  );
}
